import { useUser } from "@/components/UserProvider";
import { useHasBlockingModalOpen } from "@/hooks/useHasBlockingModalOpen";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import { useHotkeys } from "react-hotkeys-hook";

export const CREATE_MATTER_NOTE_HOTKEYS = "n, ctrl+shift+o, meta+shift+o";
export const CREATE_MATTER_TASK_HOTKEYS = "t, ctrl+shift+k, meta+shift+k";

function isTypingTarget(
  event: KeyboardEvent | ReactKeyboardEvent | undefined,
): boolean {
  const nativeEvent =
    event && "nativeEvent" in event ? event.nativeEvent : event;
  const targets = nativeEvent?.composedPath() ?? [nativeEvent?.target];
  return targets.some(
    (target) =>
      target instanceof HTMLElement &&
      (target.isContentEditable ||
        ["INPUT", "TEXTAREA", "SELECT", "IFRAME"].includes(target.tagName)),
  );
}

export const MatterCreateHotkeys = ({ matterId }: { matterId: string }) => {
  const { hasActionPermission } = useUser();
  const { openNoteModal, openTaskModal } = useGlobalModal();
  const hasBlockingModalOpen = useHasBlockingModalOpen();

  const hasNoteCreatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matter,
    ACTION_PERMISSION_VALUES.update,
  );
  const hasTaskCreatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matter,
    ACTION_PERMISSION_VALUES.update,
  );

  useHotkeys(
    CREATE_MATTER_NOTE_HOTKEYS,
    (event) => {
      if (isTypingTarget(event) || hasBlockingModalOpen()) {
        return;
      }
      event.preventDefault();
      openNoteModal({
        matterId,
        editing: false,
        initialTab: "details",
      });
    },
    {
      enabled: hasNoteCreatePermission,
    },
    [matterId, hasBlockingModalOpen, hasNoteCreatePermission, openNoteModal],
  );

  useHotkeys(
    CREATE_MATTER_TASK_HOTKEYS,
    (event) => {
      if (isTypingTarget(event) || hasBlockingModalOpen()) {
        return;
      }
      event.preventDefault();
      openTaskModal({
        matterId,
        editing: false,
        initialTab: "details",
        hideMatterField: true,
      });
    },
    {
      enabled: hasTaskCreatePermission,
    },
    [matterId, hasBlockingModalOpen, hasTaskCreatePermission, openTaskModal],
  );

  return null;
};
